<script setup>
import { useCampaignRules } from "@/composables/campaign-rules";
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import CampaignRulesModal from "./campaignRulesModal.vue";
import { notificationsMixin } from "@/mixins";
import MsgBoxConfirm from "@/components/msg-box-confirm.vue";
import AppSwitchToggle from "@/common-components/ui-elements/app-switch-toggle.vue";

const {
  list: campaignRulesList,
  total: campaignRulesListTotal,
  loading: loadingCampaignRules,
  fetchOrgCampaignRulesList,
  deleteOrgCampaignRules,
  updateOrgCampaignRules,
} = useCampaignRules();

const vm = getCurrentInstance();
const $t = vm.proxy.$t.bind(vm.proxy);
const cId = vm.proxy.$store.state.campaign.campaign.data.id;
const notificationsMixinHandleCatch =
  notificationsMixin.methods.$_notificationsMixin_handleCatch.bind(vm.proxy);
const notificationsMixinMakeToastCompat =
  notificationsMixin.methods.$_notificationsMixin_makeToastCompat.bind(vm.proxy);

const campaignRulesListPage = ref(1);
const campaignRulesListPerPage = 10;

onMounted(() => {
  fetchOrgCampaignRulesList(cId, {
    page: campaignRulesListPage.value,
    per_page: campaignRulesListPerPage,
  });
});

watch(campaignRulesListPage, (v) => {
  fetchOrgCampaignRulesList(cId, {
    page: v,
    per_page: campaignRulesListPerPage,
  });
});

function onSubmitCampaignRuleModal(hide) {
  hide();
  fetchOrgCampaignRulesList(cId, {
    page: campaignRulesListPage.value,
    per_page: campaignRulesListPerPage,
  });
}

function onDeleteRule(id) {
  deleteOrgCampaignRules(cId, id)
    .then(() => {
      notificationsMixinMakeToastCompat({
        group: "Notification",
        title: $t("edit_campaign.success_delete_campaign_rule", "Deleted"),
      });
    })
    .catch((e) => notificationsMixinHandleCatch(e))
    .finally(() => {
      fetchOrgCampaignRulesList(cId, {
        page: campaignRulesListPage.value,
        per_page: campaignRulesListPerPage,
      });
    });
}

const campaignRuleListFields = computed(() => {
  return [
    {
      key: "id",
      label: $t("edit_campaign.campaign_rules_rule_id", "ID"),
    },
    {
      key: "campaign_id",
      label: $t("edit_campaign.campaign_rules_campaign_id", "Campaign ID"),
    },
    {
      key: "action",
      label: $t("edit_campaign.campaign_rules_action", "Action"),
    },
    {
      key: "rule_type",
      label: $t("edit_campaign.campaign_rules_rule_type", "Type"),
    },
    {
      key: "meta.percentage",
      label: $t("edit_campaign.campaign_rules_meta_percentage", "Percentage"),
    },
    {
      key: "template_id",
      label: $t("edit_campaign.campaign_rules_template_id", "Template ID"),
    },
    {
      key: "active",
      label: $t("edit_campaign.campaign_rules_active", "Active"),
    },
    {
      key: "actions",
      label: $t("edit_campaign.campaign_rules_actions", "Actions"),
    },
  ];
});

async function setActive(item) {
  const previousActiveState = !item.active;

  try {
    await updateOrgCampaignRules(cId, item.id, item);

    notificationsMixinMakeToastCompat({
      group: "Notification",
      title: $t("edit_campaign.success_save_campaign_rule", "Saved"),
    });

    return;
  } catch (e) {
    item.active = previousActiveState;
    notificationsMixinHandleCatch(e);
  }
}
</script>

<template>
  <div>
    <div class="campaign-edit-title">
      <h2 class="h2-like">{{ $t("edit_campaign.campaign_rules", "Template Rules") }}</h2>
      <CampaignRulesModal @submit="onSubmitCampaignRuleModal" v-slot="{ show, loading }">
        <button
          @click="() => show()"
          type="button"
          :disabled="loading"
          :class="{
            disabled: loading,
          }"
          class="bttn bttn--lg bttn--orange"
        >
          {{ $t("edit_campaign.campaign_rules_create_rule", "Create") }}
        </button>
      </CampaignRulesModal>
    </div>
    <p class="tw-mt-4 tw-mb-[40px]">
      {{ $t("edit_campaign.campaign_rules_description", "Rules for email and SMS templates") }}
    </p>
    <b-card class="!tw-border-0" no-body>
      <b-table
        class="!tw-mb-5"
        id="campaign-rules-list"
        :busy="loadingCampaignRules"
        :items="campaignRulesList.data"
        :fields="campaignRuleListFields"
      >
        <template #cell(active)="{ item }">
          <AppSwitchToggle v-model="item.active" @change="setActive(item)" plate />
        </template>
        <template #cell(actions)="{ item }">
          <div class="tw-flex tw-items-center tw-gap-3">
            <CampaignRulesModal @submit="onSubmitCampaignRuleModal" v-slot="{ show, loading }">
              <button
                :disabled="loading"
                :aria-label="$t('edit_campaign.campaign_rules_edit_rule', 'Edit')"
                class="bttn bttn-round"
                @click="show(item)"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                  />
                  <path d="M0 0h24v24H0z" fill="none" />
                </svg>
              </button>
            </CampaignRulesModal>
            <MsgBoxConfirm
              as="button"
              :disabled="loadingCampaignRules"
              :aria-label="$t('edit_campaign.campaign_rules_delete', 'Delete')"
              :title="$t('edit_campaign.campaign_rules_delete_confirm', 'Delete rule?')"
              class="bttn bttn-round"
              @confirm="onDeleteRule(item.id)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path
                  d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                />
                <path d="M0 0h24v24H0z" fill="none" />
              </svg>
            </MsgBoxConfirm>
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="campaignRulesList.data?.length"
        v-model="campaignRulesListPage"
        :total-rows="campaignRulesListTotal"
        :per-page="campaignRulesListPerPage"
        aria-controls="campaign-rules-list"
      ></b-pagination>
    </b-card>
  </div>
</template>
